import { queryClient } from "@/src/config/react-query";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import {
  JoinRequestPayload,
  NotificationItem,
  NotificationPayload,
} from "@/src/models/notification.model";
import {
  useRemoveGroupMembership,
  useUpdateGroupMembership,
} from "@/src/services/api/groups/groups.hooks";
import { useMarkNotificationAsRead } from "@/src/services/api/notification/notification.hooks";
import {
  cleanActorName,
  getAvatarInitial,
  getNotificationMessage,
  parseNotificationPayload,
} from "@/src/utils/notification.utils";
import { useBlocksUser } from "@/src/services/users.blocks.api";
import { formatUserName } from "@/src/utils/string.utils";
import { useTheme } from "@/src/styles/theme-provider";
import { useRouter } from "expo-router";
import { More } from "iconsax-react-nativejs";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  GestureResponderEvent,
  Image,
  Modal,
  Pressable,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";

export const getRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Types whose row carries Accept / Decline.
 *
 * The buttons are an *outstanding* decision, so they only belong on a row that
 * has not been acted on. Read is the marker for "acted on": accepting or
 * declining marks the row read (see `handleAccept`/`handleDecline`), which is
 * why the caller pairs this with `isUnread` before rendering them. Without that
 * pairing an admin who accepted a request still saw a live Accept button on the
 * All tab, and pressing it re-ran the membership update.
 */
const ACTIONABLE_TYPES = ["join_request", "event_invite"] as const;

const showsActionButtons = (type: string) =>
  (ACTIONABLE_TYPES as readonly string[]).includes(type);

export const getNavigationTarget = (
  parsedPayload: NotificationPayload | null,
  router: ReturnType<typeof useRouter>,
) => {
  if (
    !parsedPayload ||
    !("targetEntityId" in parsedPayload) ||
    !parsedPayload.targetEntityId
  )
    return;
  switch (parsedPayload.type) {
    case "join_request":
      router.push({
        pathname: "/groups/[id]",
        params: {
          id: parsedPayload.targetEntityId,
          tab: "members",
          membersTab: "pending",
        },
      });
      break;
    case "added_to_group":
      router.push({
        pathname: "/groups/[id]",
        params: { id: parsedPayload.targetEntityId },
      });
      break;
    case "post_reaction":
    case "post_comment":
    case "post_share":
    case "post_sticker":
      router.push({
        pathname: "/feeds",
        params: { post_id: parsedPayload.targetEntityId },
      });
      break;
    case "mention":
      router.push({
        pathname: "/feeds",
        params: {
          post_id: parsedPayload.targetEntityId,
          ...(parsedPayload.commentId
            ? { comment_id: parsedPayload.commentId }
            : {}),
        },
      });
      break;
    case "event_created":
    case "event_invite":
      router.push({
        pathname: "/events/[id]",
        params: { id: parsedPayload.targetEntityId },
      });
      break;
    case "wiki_article_created":
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: parsedPayload.targetEntityId },
      });
      break;
    case "wiki_article_updated":
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: parsedPayload.targetEntityId },
      });
      break;
    case "news_article_created":
      router.push({
        pathname: "/(tabs)/news/news-detail",
        params: { newsId: parsedPayload.targetEntityId },
      });
      break;
    case "news_article_updated":
      router.push({
        pathname: "/(tabs)/news/news-detail",
        params: { newsId: parsedPayload.targetEntityId },
      });
      break;
    default:
      break;
  }
};

const ActionButtons = ({
  onAccept,
  onDecline,
  isPending,
}: {
  onAccept: () => void;
  onDecline: () => void;
  isPending: boolean;
}) => {
  const { t } = useTranslation();

  // The buttons sit inside the row's own Pressable. On web the click keeps
  // bubbling to it, so without stopping propagation Accept also fired
  // `handleRowPress` and navigated away from the row mid-mutation.
  // Both the event and the method are optional: a synthetic press (and
  // `fireEvent.press`) invokes the handler with no argument at all, and
  // stopping propagation is a nicety — never a precondition for the action
  // itself, which must still run.
  const contain =
    (handler: () => void) => (event?: GestureResponderEvent) => {
      event?.stopPropagation?.();
      handler();
    };

  return (
    <View className="flex-row gap-2 mt-2">
      {isPending ? (
        <ActivityIndicator size="small" />
      ) : (
        <>
          <TouchableOpacity
            className="bg-primary px-5 py-1.5 rounded-full hover:opacity-90"
            onPress={contain(onAccept)}
          >
            <Text className="text-chalk text-xs font-medium">
              {t("LABEL_ACCEPT")}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            className="border border-primary px-5 py-1.5 rounded-full hover:bg-ash-3"
            onPress={contain(onDecline)}
          >
            <Text className="text-primary text-xs font-medium">
              {t("LABEL_DECLINE")}
            </Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

export const NotificationRow = ({ item }: { item: NotificationItem }) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  const router = useRouter();
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [actionPending, setActionPending] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const moreButtonRef = useRef<View>(null);

  const parsedPayload: NotificationPayload | null =
    parseNotificationPayload(item);
  const message = getNotificationMessage(item);

  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutateAsync: updateGroupMembership } = useUpdateGroupMembership();
  const { mutateAsync: removeGroupMembership } = useRemoveGroupMembership();

  const handleMorePress = (e: any) => {
    e.stopPropagation();
    moreButtonRef.current?.measure((_fx, _fy, width, height, px, py) => {
      setMenuPosition({ x: px - 160 + width, y: py + height });
      setOpenMenu(true);
    });
  };

  const handleAccept = async () => {
    if (!parsedPayload) return;

    // An event invite has no one-shot accept: RSVPing honours capacity, the
    // waitlist and guest count, all of which live on the event screen. So
    // accepting here retires the notification and opens the event, where the
    // real join control is. (No producer in the app emits `event_invite` yet —
    // the branch exists so the row is never a dead button if one starts to.)
    if (parsedPayload.type === "event_invite") {
      markAsRead(item.id);
      getNavigationTarget(parsedPayload, router);
      return;
    }

    if (parsedPayload.type !== "join_request") return;
    const payload = parsedPayload as JoinRequestPayload;
    if (!payload.membershipId) return;
    setActionPending(true);
    try {
      await updateGroupMembership({
        itemId: payload.membershipId,
        status: MembershipStatus.ACTIVE,
      });

      // The decision is made, so the row must stop asking for it: marking read
      // is what hides Accept/Decline (see ACTIONABLE_TYPES) and drops the row
      // off the Unread tab and the nav badge.
      //
      // Ordered before the cache invalidations on purpose. The membership has
      // already changed server-side by this point, so the notification is
      // settled whatever happens next; leaving this until after the
      // invalidations put it behind four calls that, if any of them threw, sent
      // the whole block to `catch` and left an answered request still showing
      // live Accept/Decline.
      markAsRead(item.id);

      queryClient.invalidateQueries({ queryKey: ["getGroupMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount"] });
      queryClient.invalidateQueries({ queryKey: ["getPendingMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds"] });
    } catch (e) {
      console.error(e);
    } finally {
      setActionPending(false);
    }
  };

  const handleDecline = async () => {
    if (!parsedPayload) return;

    if (parsedPayload.type === "event_invite") {
      markAsRead(item.id);
      return;
    }

    if (parsedPayload.type !== "join_request") return;
    const payload = parsedPayload as JoinRequestPayload;
    if (!payload.membershipId) return;
    setActionPending(true);
    try {
      await removeGroupMembership(payload.membershipId);

      // Same ordering as `handleAccept`, for the same reason.
      markAsRead(item.id);

      queryClient.invalidateQueries({ queryKey: ["getGroupMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount"] });
      queryClient.invalidateQueries({ queryKey: ["getPendingMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberIds"] });
    } catch (e) {
      console.error(e);
    } finally {
      setActionPending(false);
    }
  };

  const actorAvatar =
    parsedPayload && "actorAvatar" in parsedPayload
      ? parsedPayload.actorAvatar
      : undefined;
  const actorName =
    parsedPayload && "actorName" in parsedPayload
      ? (parsedPayload.actorName ?? "")
      : "";
  const actorId =
    parsedPayload && "actorId" in parsedPayload
      ? parsedPayload.actorId
      : undefined;

  // The payload's name and avatar are a snapshot from when it was created.
  // A blank one there used to leave the row showing "?" forever.
  // So look the actor up now and prefer what they have today.
  // Disabled automatically when the payload carries no actorId.
  const { data: actor } = useBlocksUser(actorId);

  const isUnread = !item.isRead;

  // First name + last name. BlocksUser has no displayName field.
  // Falls back to the payload's stored name, which producers build the same way.
  const actorFullName =
    formatUserName(actor?.firstName, actor?.lastName) ||
    cleanActorName(actorName);
  const avatarUri = actor?.profileImageUrl ?? actorAvatar;

  // A stored avatar URL can 404 — profile images outlive the blobs they point at.
  // Branching on the URL being present is not enough: a dead URL is still truthy,
  // so the Image rendered an empty box and the initials below never got a chance.
  const renderAvatar = (size: number) => {
    const box = { width: size, height: size, borderRadius: size / 2 };

    if (avatarUri && !avatarFailed) {
      return (
        <Image
          source={{ uri: avatarUri }}
          style={box}
          onError={() => setAvatarFailed(true)}
        />
      );
    }

    // No picture and no name leaves nothing to draw a letter from.
    // A default face reads better here than a "?" placeholder.
    if (!actorFullName) {
      return (
        <Image
          source={require("@/src/assets/images/default-avatar.png")}
          style={box}
        />
      );
    }

    return (
      <View style={box} className="bg-ash items-center justify-center">
        <Text className="text-slate font-medium text-base">
          {getAvatarInitial(actorFullName)}
        </Text>
      </View>
    );
  };

  // With Delete gone, "Mark as read" is the only entry — so on an already-read
  // row the menu would open empty. Hide the trigger instead of showing a blank
  // popover.
  const moreButton = isUnread ? (
    <View ref={moreButtonRef}>
      <Pressable
        onPress={handleMorePress}
        hitSlop={8}
        className="p-1 active:opacity-70 hover:bg-ash-3 hover:rounded-full"
      >
        <More size={16} color={themeVars["--color-ash-1"]} rotation={90} />
      </Pressable>
    </View>
  ) : null;

  // A row that asks for a decision. Its Accept/Decline are the only things that
  // may retire it, so it is exempt from the tap-to-read rule below.
  const isActionable = showsActionButtons(parsedPayload?.type ?? "");

  // Read means "already decided", so the buttons come down once the row is read
  // — whether that happened here, via "Mark as read", or via "Mark all as
  // read". This is what stops a settled join request from offering Accept again
  // on the All tab.
  const actionButtons = isActionable && isUnread && (
    <ActionButtons
      onAccept={handleAccept}
      onDecline={handleDecline}
      isPending={actionPending}
    />
  );

  // Tapping the row is the primary way users read a notification — the "..."
  // menu's explicit "Mark as read" is a secondary path for someone who doesn't
  // want to navigate away. Before this, only that menu (and join-request
  // accept/decline) ever called markAsRead, so opening a notification by
  // tapping it left it — and the unread badge count derived from it — unread
  // forever, until the user separately hit "Mark all as read".
  //
  // Actionable rows are the exception: pressing anywhere outside Accept/Decline
  // must NOT mark them read. Reading a join request is not answering it, and
  // marking it read would strip the very buttons the user navigated over to
  // use. Such a row is retired by acting on it (or by an explicit
  // "Mark as read"), never by being looked at.
  const handleRowPress = () => {
    if (isUnread && !isActionable) markAsRead(item.id);
    getNavigationTarget(parsedPayload, router);
  };

  return (
    <>
      {isMobile ? (
        // Modern feed row: borderless, edge-to-edge, unread = subtle tint + dot.
        <Pressable
          style={varStyle}
          onPress={handleRowPress}
          className={`flex-row gap-3 px-3 py-3 rounded-2xl active:bg-ash-3 hover:bg-ash-3 ${
            isUnread ? "bg-primary-2/60" : ""
          }`}
        >
          {renderAvatar(48)}

          <View className="flex-1">
            <Text className="text-sm text-onyx leading-5" numberOfLines={3}>
              {message}
            </Text>
            <Text
              className={`text-xs mt-1 ${
                isUnread ? "text-primary font-medium" : "text-ash-1"
              }`}
            >
              {getRelativeTime(item.createdTime)}
            </Text>
            {actionButtons}
          </View>

          <View className="items-center gap-3 pl-1">
            {moreButton}
            {isUnread && (
              <View className="w-2.5 h-2.5 rounded-full bg-primary" />
            )}
          </View>
        </Pressable>
      ) : (
        <Pressable
          style={varStyle}
          onPress={handleRowPress}
          className="bg-chalk mb-2 rounded-2xl border border-ash-3 active:bg-ash-3 web:hover:bg-ash-3"
        >
          <View className="flex-row items-center px-4 py-2">
            <View className="mr-3">{renderAvatar(40)}</View>

            <View className="flex-1">
              <Text className="text-sm text-onyx leading-5" numberOfLines={2}>
                {message}
              </Text>
              {actionButtons}
            </View>

            <View className="items-end ml-3 gap-1">
              <Text className="text-xs text-ash-1">
                {getRelativeTime(item.createdTime)}
              </Text>
              {moreButton}
            </View>
          </View>
        </Pressable>
      )}

      <Modal
        visible={openMenu}
        transparent
        animationType="none"
        onRequestClose={() => setOpenMenu(false)}
      >
        <Pressable style={{ flex: 1 }} onPress={() => setOpenMenu(false)}>
          <View
            style={{
              position: "absolute",
              top: menuPosition.y,
              left: menuPosition.x,
              backgroundColor: themeVars["--color-chalk-default"],
              borderWidth: 1,
              borderColor: themeVars["--color-ash-3"],
              borderRadius: 12,
              minWidth: 160,
              overflow: "hidden",
              elevation: 20,
              shadowColor: themeVars["--color-shadow-dark"],
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 1,
              shadowRadius: 8,
            }}
          >
            {/* Delete was removed: Blocks exposes no per-user notification
                delete route, so the item could never do anything. Its handler
                only closed this menu, which read as a broken feature. Restore it
                when the platform ships an endpoint — the confirmed-missing
                surface is documented in the v4 migration notes. */}
            <TouchableOpacity
              onPress={() => {
                markAsRead(item.id);
                setOpenMenu(false);
              }}
              className="hover:bg-ash-3"
              style={{ paddingHorizontal: 16, paddingVertical: 12 }}
            >
              <Text
                style={{
                  fontSize: 14,
                  color: themeVars["--color-onyx-default"],
                }}
              >
                {t("BTN_MARK_AS_READ")}
              </Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};
